import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { isEqual } from 'lodash';
import { useContext, useEffect, useState, useRef } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { createUseStyles } from 'react-jss';
import { Box } from '@wds/box';
import { Modal } from '@wds/modal';
import useInterval from '../shared/useInterval';
import KeywordGeneration from './KeywordGeneration';
import KeywordSelection from './KeywordSelection';
import TrainingInProgress from './TrainingInProgress';
import TrainingError from './TrainingError';
import { OECJobState, TRAINING_STEP, OECTrainingStatus, OECDiscardKeywordsModal, OECDiscardKeywordGenerationModal } from '../types';
import { transformKeywords, transformBackKeywords, formatKeywords, formatKeywordsForPersistence, validateKeywordsForTheme, haveKeywordsChanged } from '../lib';
import OpenEndedCodingContext from '../OpenEndedCodingContext';
import { useTrainingActions, useThemesActions, useRetraining, useRetrainingActions, useGeneralActions, useFeedbackActions, useGeneral, useFeedback, useThemes, useTraining } from '../Context';
import { validateKeywords } from '../validators';
import getStatus from './lib';
import Footer from './Footer';
import Header from './Header';
import ErrorToast from '../shared/ErrorToast';
import { keywordsForQuestion } from '../graph/fetchKeywords';
import { generateOecKeywordTags } from '../graph/generateKeywords';
import { updateOecKeywordTags } from '../graph/updateKeywords';
import { createOecJob } from '../graph/createJob';
import { getOecJobs } from '../graph/getJobs';
import { getOecResponseSummary } from '../graph/getResponseSummaryCounts';
import { sendOecKeywordFeedback } from '../graph/sendKeywordFeedback';
import DiscardKeywordGenerationModal from './DiscardKeywordGenerationModal';
import DiscardKeywordsModal from './DiscardKeywordsModal';
const FETCH_KEYWORDS_INTERVAL = 6000;
const FETCH_JOBS_INTERVAL = 10000;
const FETCH_KEYWORDS_RETRY_LIMIT = 20;
// We want to ensure the modal has a fixed height.
// and doesn't increase in size once responses are loaded.
const bodyHeight = '90vh';
const useStyles = createUseStyles({
  container: {
    height: bodyHeight,
    display: 'flex',
    justifyContent: 'center'
  }
});
const TrainingFlow = ({
  show,
  questionText
}) => {
  const classes = useStyles();
  const {
    reloadResponses,
    handleTrainingFinished,
    setShowOecModal
  } = useContext(OpenEndedCodingContext);
  const {
    surveyId,
    questionId,
    isSolutions,
    showDiscardKeywordGenerationModal,
    showDiscardKeywordsModal
  } = useGeneral();
  const {
    jobId,
    keywordPredictionId
  } = useFeedback();
  const {
    themes,
    keywords
  } = useThemes();
  const {
    trainingStep,
    unsavedKeywords,
    generatedKeywords,
    showTrainingErrorToast
  } = useTraining();
  const {
    setTrainingStep,
    setSummaryCounts,
    setShowTrainingErrorToast,
    setUnsavedKeywords,
    setShowTrainingScreen,
    setMultiple: setMultipleTrainingFields
  } = useTrainingActions();
  const {
    setKeywords
  } = useThemesActions();
  const {
    notEnoughThemesCreated
  } = useRetraining();
  const {
    setTrainingStatus
  } = useRetrainingActions();
  const {
    setShowDiscardKeywordGenerationModal,
    setShowDiscardKeywordsModal
  } = useGeneralActions();
  const {
    setKeywordPredictionId,
    setJobId,
    setAutotaggingPredictionId
  } = useFeedbackActions();
  // to make sure we don't make duplicate requests
  const [isGeneratingKeywords, setIsGeneratingKeywords] = useState(false);
  const fetchKeywordsAttempts = useRef(0);
  const keywordsChanged = haveKeywordsChanged({
    generatedKeywords: transformKeywords({
      keywords
    }),
    unsavedKeywords
  });
  const {
    isFetching,
    isGenerating,
    isTraining,
    isLoaded,
    isViewing,
    isTrained,
    isTrainingError
  } = getStatus(trainingStep);
  const showFooter = isLoaded || isViewing;
  // queries
  const [fetchKeywords, {
    loading: keywordsLoading,
    called: fetchKeywordsCalled
  }] = useLazyQuery(keywordsForQuestion, {
    fetchPolicy: 'no-cache',
    onCompleted: ({
      getOecKeywords: {
        predictionId,
        tags: newKeywords
      }
    }) => {
      // same prediction ID
      if (predictionId === keywordPredictionId || newKeywords) {
        const kw = newKeywords.map(k => formatKeywords(k));
        // update state to loaded only if fetch is following a generate request.
        // disable the button start auto-tagging if user's intent is to view keywords.
        setMultipleTrainingFields({
          trainingStep: isViewing ? TRAINING_STEP.KEYWORDS_VIEWING : TRAINING_STEP.KEYWORDS_LOADED,
          unsavedKeywords: transformKeywords({
            keywords: kw
          }),
          generatedKeywords: newKeywords.map(k => {
            return {
              id: k.id,
              label: k.label,
              keywords: k.keywords
            };
          })
        });
        setKeywords(kw);
        // hide modal if shown
        if (showDiscardKeywordGenerationModal !== OECDiscardKeywordGenerationModal.HIDE_KEYWORDS_MODAL) {
          setShowDiscardKeywordGenerationModal(OECDiscardKeywordGenerationModal.HIDE_KEYWORDS_MODAL);
        }
      }
    },
    onError: () => {
      // we want to keep polling if there was an error
      // fetching keywords until we hit a retry limit
      if (fetchKeywordsAttempts.current >= FETCH_KEYWORDS_RETRY_LIMIT) {
        setTrainingStep(TRAINING_STEP.TRAINING_ERROR);
      } else {
        fetchKeywordsAttempts.current += 1;
      }
    }
  });
  const [fetchTrainingJobs] = useLazyQuery(getOecJobs, {
    fetchPolicy: 'no-cache',
    onCompleted: ({
      getOecJobs: data
    }) => {
      // job we just triggered
      const job = data.jobs.find(j => j.jobId === jobId);
      // if the job has finished
      if ((job === null || job === void 0 ? void 0 : job.state) === OECJobState.SUCCESS) {
        if (handleTrainingFinished) {
          handleTrainingFinished();
        }
        setTrainingStep(TRAINING_STEP.TRAINING_FINISHED);
        setTrainingStatus(OECTrainingStatus.TRAINING_COMPLETE);
        setAutotaggingPredictionId(job.predictionId);
      }
      // job error
      if ((job === null || job === void 0 ? void 0 : job.state) === OECJobState.FAILED) {
        setTrainingStep(TRAINING_STEP.TRAINING_ERROR);
      }
    },
    onError: () => {
      setTrainingStep(TRAINING_STEP.TRAINING_ERROR);
    }
  });
  const [fetchSummaryCounts, {
    called: fetchCountsCalled
  }] = useLazyQuery(getOecResponseSummary, {
    onCompleted: ({
      getOecResponseSummary: {
        counts
      }
    }) => {
      setSummaryCounts(counts);
      reloadResponses();
      setShowTrainingScreen(false);
    },
    onError: () => {
      setShowTrainingErrorToast(true);
      setShowTrainingScreen(false);
    }
  });
  // mutations
  const [generateKeywords] = useMutation(generateOecKeywordTags, {
    onCompleted: ({
      generateOecKeywordTags: {
        predictionId,
        success
      }
    }) => {
      if (success) {
        setIsGeneratingKeywords(false);
        setTrainingStep(TRAINING_STEP.KEYWORD_FETCHING);
        setKeywordPredictionId(predictionId);
      }
    },
    onError: () => {
      // show an error screen if there is an error generating keywords
      setTrainingStep(TRAINING_STEP.TRAINING_ERROR);
    }
  });
  const [createTrainingJob] = useMutation(createOecJob, {
    onCompleted: ({
      createOecJob: {
        job
      }
    }) => {
      setTrainingStep(TRAINING_STEP.TRAINING_IN_PROGRESS);
      setJobId(job.jobId);
    },
    onError: () => {
      setShowTrainingErrorToast(true);
    }
  });
  const [updateKeywords] = useMutation(updateOecKeywordTags, {
    onCompleted: ({
      updateOecKeywordTags: {
        success
      }
    }) => {
      if (success) {
        void createTrainingJob({
          variables: {
            input: {
              surveyId,
              oecData: {
                questionId
              }
            }
          }
        });
      }
    },
    onError: () => {
      // should create an error state here, train the model anyways
    }
  });
  const [sendKeywordFeedback] = useMutation(sendOecKeywordFeedback, {
    onCompleted: () => {},
    onError: () => {}
  });
  useEffect(() => {
    if (!show && isTrained) {
      setShowTrainingScreen(false);
    }
  }, [show, isTrained, setShowTrainingScreen]);
  // periodically fetch keywords
  useInterval(() => {
    if (!isFetching) {
      return;
    }
    fetchKeywords({
      variables: {
        surveyId,
        questionId
      }
    });
  }, isFetching ? FETCH_KEYWORDS_INTERVAL : 0);
  // periodically fetch jobs
  useInterval(() => {
    if (!isTraining) {
      return;
    }
    fetchTrainingJobs({
      variables: {
        surveyId,
        questionId
      }
    });
  }, isTraining ? FETCH_JOBS_INTERVAL : 0);
  // generate keywords initially
  if (isGenerating && !isGeneratingKeywords) {
    setIsGeneratingKeywords(true);
    void generateKeywords({
      variables: {
        input: {
          surveyId,
          questionId,
          tagIds: themes.map(theme => theme.id)
        }
      }
    });
  }
  if (isViewing && keywords.length === 0 && !fetchKeywordsCalled) {
    void fetchKeywords({
      variables: {
        surveyId,
        questionId
      }
    });
  }
  // fetch counts when training finished
  if (isTrained && !fetchCountsCalled) {
    void fetchSummaryCounts({
      variables: {
        surveyId,
        questionId,
        isSolutions
      }
    });
  }
  const handleValidKeywords = validatedKeywords => {
    const transformedKeywords = transformBackKeywords({
      keywords: validatedKeywords
    });
    const savedKeywords = formatKeywordsForPersistence({
      themes,
      keywords: transformedKeywords
    });
    // Persist keywords in the backend
    void updateKeywords({
      variables: {
        input: {
          surveyId,
          questionId,
          tags: savedKeywords
        }
      }
    })
    // Send keyword feedback to the backend
    .then(() => {
      // sometimes a keyword prediction ID isn't present
      if (keywordPredictionId) {
        void sendKeywordFeedback({
          variables: {
            input: {
              predictionId: keywordPredictionId,
              surveyId,
              questionId,
              autoGeneratedState: generatedKeywords,
              modifiedState: savedKeywords
            }
          }
        });
      }
    });
    // Update the global keywords
    setKeywords(transformedKeywords);
  };
  const handleFooterNext = () => {
    // On keyword verification screen
    if (isLoaded) {
      const validatedKeywords = validateKeywords({
        keywords: unsavedKeywords
      });
      const validationError = validatedKeywords.filter(k => k.error).length > 0;
      // Don't continue if there is an issue with the keywords entered
      if (validationError) {
        setUnsavedKeywords(validatedKeywords);
      } else {
        handleValidKeywords(validatedKeywords);
      }
      return;
    }
    // If training has finished go back to manual tagging
    if (isTrained || isTrainingError) {
      setShowTrainingScreen(false);
      if (isTrained) {
        reloadResponses();
      }
    }
  };
  const handleFooterBack = () => {
    // Display a confirmation modal
    if (isGenerating || isFetching) {
      setShowDiscardKeywordGenerationModal(OECDiscardKeywordGenerationModal.SHOW_KEYWORDS_MODAL_BACK);
      return;
    }
    // Display a confirmation modal
    if (keywordsChanged) {
      setShowDiscardKeywordsModal(OECDiscardKeywordsModal.SHOW_KEYWORDS_MODAL_BACK);
      return;
    }
    // Could have outdated tags
    if (isTrained) {
      reloadResponses();
    }
    setShowTrainingScreen(false);
  };
  const onUpdateKeywords = ({
    newKeywords,
    themeId
  }) => {
    const currentKeywords = validateKeywordsForTheme({
      keywords: unsavedKeywords,
      newKeywords,
      themeId
    });
    setMultipleTrainingFields({
      unsavedKeywords: currentKeywords,
      trainingStep: !isEqual(keywords, transformBackKeywords({
        keywords: currentKeywords
      })) ? TRAINING_STEP.KEYWORDS_LOADED : TRAINING_STEP.KEYWORDS_VIEWING
    });
  };
  const TrainingFlowComponent = () => {
    if (isGenerating || isFetching) {
      return _jsx(KeywordGeneration, {});
    }
    if (isLoaded || isViewing) {
      return _jsx(KeywordSelection, {
        questionText: questionText,
        themes: themes,
        keywords: unsavedKeywords,
        keywordsLoading: keywordsLoading,
        setKeywords: onUpdateKeywords
      });
    }
    if (isTraining) {
      return _jsx(TrainingInProgress, {});
    }
    if (isTrainingError) {
      return _jsx(TrainingError, {});
    }
    return _jsx(TrainingInProgress, {});
  };
  return _jsxs(_Fragment, {
    children: [_jsx(Header, {
      status: trainingStep
    }), _jsx(Modal.Body, {
      padding: false,
      children: _jsx(Box, {
        className: classes.container,
        children: TrainingFlowComponent()
      })
    }), showFooter && _jsx(Modal.Footer, {
      children: _jsx(Footer, {
        status: trainingStep,
        handleNext: handleFooterNext,
        hasInsufficientThemes: notEnoughThemesCreated,
        onClose: handleFooterBack,
        keywords: unsavedKeywords
      })
    }), _jsx(ErrorToast, {
      show: showTrainingErrorToast,
      dismissible: true,
      onClose: () => setShowTrainingErrorToast(false)
    }), _jsx(DiscardKeywordGenerationModal, {
      show: showDiscardKeywordGenerationModal !== OECDiscardKeywordGenerationModal.HIDE_KEYWORDS_MODAL,
      onCancel: () => {
        setShowDiscardKeywordGenerationModal(OECDiscardKeywordGenerationModal.HIDE_KEYWORDS_MODAL);
      },
      onExit: () => {
        setShowDiscardKeywordGenerationModal(OECDiscardKeywordGenerationModal.HIDE_KEYWORDS_MODAL);
        setShowTrainingScreen(false);
        // user intended to close OEC flow
        if (showDiscardKeywordGenerationModal === OECDiscardKeywordGenerationModal.SHOW_KEYWORDS_MODAL_CLOSED) {
          setShowOecModal(false);
        }
      }
    }), _jsx(DiscardKeywordsModal, {
      show: showDiscardKeywordsModal !== OECDiscardKeywordsModal.HIDE_KEYWORDS_MODAL,
      onCancel: () => {
        setShowDiscardKeywordsModal(OECDiscardKeywordsModal.HIDE_KEYWORDS_MODAL);
      },
      onDiscard: () => {
        // reset keywords
        setMultipleTrainingFields({
          unsavedKeywords: transformKeywords({
            keywords
          }),
          showTrainingScreen: false
        });
        setShowDiscardKeywordsModal(OECDiscardKeywordsModal.HIDE_KEYWORDS_MODAL);
        // user intended to close OEC flow
        if (showDiscardKeywordsModal === OECDiscardKeywordsModal.SHOW_KEYWORDS_MODAL_CLOSED) {
          setShowOecModal(false);
        }
      }
    })]
  });
};
export default TrainingFlow;